// hooks/usePlanTracker.js - SIMPLE VERSION FOR LAUNCH

import { useState, useEffect } from 'react';
import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { getPlanLimits, PLAN_LIMITS } from '../constants/planLimits';

export const usePlanTracker = (userId) => {
  const [userPlan, setUserPlan] = useState('free');
  const [usage, setUsage] = useState({
    prompt: 0,
    audio: 0,
    mp4Downloads: 0
  });
  const [lastResetDate, setLastResetDate] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const initializeUsage = async () => {
    if (!userId) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const usageRef = doc(db, 'usage', userId);
      const usageDoc = await getDoc(usageRef);

      if (usageDoc.exists()) {
        const data = usageDoc.data();
        
        setUserPlan(data.planType || 'free');
        setUsage({
          prompt: data.monthlyUsage?.prompt || 0,
          audio: data.monthlyUsage?.audio || 0,
          mp4Downloads: data.mp4Downloads || 0
        });
        setLastResetDate(data.lastResetDate?.toDate() || new Date());

        // ✅ ONLY reset FREE users (paid users handled by backend webhooks)
        if (data.planType === 'free') {
          await checkFreeUserReset(data);
        }

      } else {
        const newUsageDoc = {
          userId,
          planType: 'free',
          monthlyUsage: { prompt: 0, audio: 0 },
          mp4Downloads: 0,
          lastResetDate: serverTimestamp(),
          createdAt: serverTimestamp()
        };

        await setDoc(usageRef, newUsageDoc);
        setUserPlan('free');
        setUsage({ prompt: 0, audio: 0, mp4Downloads: 0 });
        setLastResetDate(new Date());
      }

    } catch (err) {
      setError(err.message || 'Failed to load usage data');
    } finally {
      setIsLoading(false);
    }
  };

  // ✅ SIMPLE: Only check FREE users for 30-day reset
  const checkFreeUserReset = async (currentData) => {
    if (!currentData.lastResetDate) return;

    const lastReset = currentData.lastResetDate.toDate();
    const now = new Date();
    const daysSinceReset = Math.floor((now - lastReset) / (1000 * 60 * 60 * 24));

    // Reset if 30+ days
    if (daysSinceReset >= 30) {
      const usageRef = doc(db, 'usage', userId);
      await updateDoc(usageRef, {
        monthlyUsage: { prompt: 0, audio: 0 },
        mp4Downloads: 0,
        lastResetDate: serverTimestamp()
      });

      setUsage({ prompt: 0, audio: 0, mp4Downloads: 0 });
      setLastResetDate(new Date());
    }
  };

  const canCreateProject = (projectType) => {
    const limits = getPlanLimits(userPlan);
    const currentUsage = usage[projectType] || 0;
    const typeLimit = limits.monthlyProjects[projectType];
    const typeUsed = currentUsage;
    const typeRemaining = typeLimit - typeUsed;

    if (typeUsed >= typeLimit) {
      let upgradeMsg = '';
      
      if (userPlan === 'free') {
        upgradeMsg = projectType === 'audio' 
          ? `Upgrade to Starter for 10 audio projects/month!`
          : `Upgrade to Starter for 60 prompt projects/month!`;
      } else if (userPlan === 'starter') {
        upgradeMsg = projectType === 'audio'
          ? `Upgrade to Creator for 50 audio projects/month!`
          : `Upgrade to Creator for 150 prompt projects/month!`;
      } else if (userPlan === 'creator') {
        upgradeMsg = projectType === 'audio'
          ? `Upgrade to Professional for 100 audio projects/month!`
          : `Upgrade to Professional for 250 prompt projects/month!`;
      }

      return {
        allowed: false,
        reason: `${userPlan.charAt(0).toUpperCase() + userPlan.slice(1)} plan ${projectType} limit reached (${typeLimit}/month). ${upgradeMsg}`,
        remaining: 0,
        limit: typeLimit,
        used: typeUsed
      };
    }

    const totalUsed = usage.prompt + usage.audio;
    const totalLimit = limits.monthlyProjects.total;
    const totalRemaining = totalLimit - totalUsed;

    if (totalUsed >= totalLimit) {
      const upgradeMsg = userPlan === 'creator' 
        ? 'Upgrade to Professional for 350 total projects/month!'
        : userPlan === 'starter'
        ? 'Upgrade to Creator for 200 total projects/month!'
        : userPlan === 'free'
        ? 'Upgrade to Starter for 70 total projects/month!'
        : '';

      return {
        allowed: false,
        reason: `Monthly total limit of ${totalLimit} projects reached. ${upgradeMsg}`,
        remaining: 0,
        limit: totalLimit,
        used: totalUsed
      };
    }

    return {
      allowed: true,
      reason: `You have ${typeRemaining} ${projectType} project${typeRemaining !== 1 ? 's' : ''} remaining (${totalRemaining} total remaining)`,
      remaining: typeRemaining,
      limit: typeLimit,
      used: typeUsed,
      totalRemaining,
      totalUsed
    };
  };

  const canDownloadMP4 = () => {
    const limits = getPlanLimits(userPlan);
    const currentDownloads = usage.mp4Downloads || 0;
    const limit = limits.mp4Downloads;
    const allowed = currentDownloads < limit;

    let reason;
    if (allowed) {
      reason = `You have ${limit - currentDownloads} MP4 download(s) remaining this month`;
    } else {
      if (userPlan === 'free') {
        reason = `You've used all ${limit} MP4 downloads this month. Upgrade to Starter for 20 MP4 downloads/month!`;
      } else if (userPlan === 'starter') {
        reason = `You've used all ${limit} MP4 downloads this month. Upgrade to Creator for 80 MP4 downloads/month!`;
      } else if (userPlan === 'creator') {
        reason = `You've used all ${limit} MP4 downloads this month. Upgrade to Professional for 150 MP4 downloads/month!`;
      } else {
        reason = `You've used all ${limit} MP4 downloads this month.`;
      }
    }

    return {
      allowed,
      reason,
      remaining: Math.max(0, limit - currentDownloads),
      limit
    };
  };

  const checkDurationLimit = (durationSeconds) => {
    const limits = getPlanLimits(userPlan);
    const maxDuration = limits.maxAudioDuration;
    const allowed = durationSeconds <= maxDuration;
    const maxMinutes = Math.floor(maxDuration / 60);

    let upgradeMessage = '';
    if (!allowed) {
      if ((userPlan === 'free' || userPlan === 'starter') && durationSeconds <= 2400) {
        upgradeMessage = ' Upgrade to Creator for 40-minute uploads!';
      } else if (userPlan === 'creator' && durationSeconds <= 3600) {
        upgradeMessage = ' Upgrade to Professional for 60-minute uploads!';
      } else {
        upgradeMessage = ' Maximum supported duration is 60 minutes.';
      }
    }

    return {
      allowed,
      reason: allowed
        ? `Duration is within your ${maxMinutes}-minute limit`
        : `Audio is too long. Your plan allows max ${maxMinutes} minutes. Current: ${Math.floor(durationSeconds / 60)} minutes.${upgradeMessage}`,
      maxDuration,
      maxMinutes
    };
  };

  const incrementUsage = async (projectType) => {
    if (!userId) return;

    try {
      const usageRef = doc(db, 'usage', userId);
      const fieldPath = `monthlyUsage.${projectType}`;
      const currentValue = usage[projectType] || 0;
      
      await updateDoc(usageRef, {
        [fieldPath]: currentValue + 1
      });

      setUsage(prev => ({
        ...prev,
        [projectType]: prev[projectType] + 1
      }));
    } catch (err) {
      throw err;
    }
  };

  const incrementMP4Download = async () => {
    if (!userId) return;

    try {
      const usageRef = doc(db, 'usage', userId);
      const currentDownloads = usage.mp4Downloads || 0;
      
      await updateDoc(usageRef, {
        mp4Downloads: currentDownloads + 1
      });

      setUsage(prev => ({
        ...prev,
        mp4Downloads: prev.mp4Downloads + 1
      }));
    } catch (err) {
      throw err;
    }
  };

  const updateUserPlan = async (newPlan) => {
    if (!userId) return;

    try {
      const usageRef = doc(db, 'usage', userId);
      
      await updateDoc(usageRef, {
        planType: newPlan,
        updatedAt: serverTimestamp()
      });

      setUserPlan(newPlan);
    } catch (err) {
      throw err;
    }
  };

  const getRemainingProjects = () => {
    const limits = getPlanLimits(userPlan);
    const promptRemaining = Math.max(0, limits.monthlyProjects.prompt - usage.prompt);
    const audioRemaining = Math.max(0, limits.monthlyProjects.audio - usage.audio);
    const totalUsed = usage.prompt + usage.audio;
    const totalRemaining = Math.max(0, limits.monthlyProjects.total - totalUsed);

    return {
      prompt: promptRemaining,
      audio: audioRemaining,
      total: totalRemaining
    };
  };

  const getUsagePercentage = (projectType = 'total') => {
    const limits = getPlanLimits(userPlan);

    if (projectType === 'total') {
      const totalUsed = usage.prompt + usage.audio;
      return Math.round((totalUsed / limits.monthlyProjects.total) * 100);
    }

    const limit = limits.monthlyProjects[projectType];
    const used = usage[projectType] || 0;
    return Math.round((used / limit) * 100);
  };

  const shouldShowUpgradePrompt = () => {
    return getUsagePercentage('total') >= 80;
  };

  const getPlanInfo = () => {
    return PLAN_LIMITS[userPlan] || PLAN_LIMITS.free;
  };

  useEffect(() => {
    if (userId) {
      initializeUsage();
    } else {
      setUserPlan('free');
      setUsage({ prompt: 0, audio: 0, mp4Downloads: 0 });
      setIsLoading(false);
    }
  }, [userId]);

  return {
    userPlan,
    usage,
    lastResetDate,
    isLoading,
    error,
    canCreateProject,
    canDownloadMP4,
    checkDurationLimit,
    getRemainingProjects,
    getUsagePercentage,
    shouldShowUpgradePrompt,
    getPlanInfo,
    incrementUsage,
    incrementMP4Download,
    updateUserPlan,
    refresh: initializeUsage
  };
};

export default usePlanTracker;